import {
  scrollbarSx_default
} from "./chunk-GD6E42CP.mjs";
import {
  getItemParams
} from "./chunk-JYXW2UNX.mjs";

// src/UberFilter/UberFilterGroup.tsx
import { useState as useState2, useEffect as useEffect2 } from "react";
import Box3 from "@mui/material/Box/index.js";
import Typography3 from "@mui/material/Typography/index.js";
import { useTheme as useTheme2 } from "@mui/material/styles/index.js";
import ExpandMoreIcon from "@mui/icons-material/esm/ExpandMore.js";

// src/UberFilter/FilterPopover.tsx
import Box2 from "@mui/material/Box/index.js";
import Popover from "@mui/material/Popover/index.js";
import Stack from "@mui/material/Stack/index.js";
import Button from "@mui/material/Button/index.js";
import Typography2 from "@mui/material/Typography/index.js";
import { useTheme } from "@mui/material/styles/index.js";

// src/UberFilter/FilterField.tsx
import { useState, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField/index.js";
import Box from "@mui/material/Box/index.js";
import Typography from "@mui/material/Typography/index.js";
import List from "@mui/material/List/index.js";
import ListItem from "@mui/material/ListItem/index.js";
import ListItemText from "@mui/material/ListItemText/index.js";
import Checkbox from "@mui/material/Checkbox/index.js";
import ListItemIcon from "@mui/material/ListItemIcon/index.js";
import Paper from "@mui/material/Paper/index.js";
import Slider from "@mui/material/Slider/index.js";
import InputAdornment from "@mui/material/InputAdornment/index.js";
import SearchIcon from "@mui/icons-material/esm/Search.js";
import { jsx, jsxs } from "react/jsx-runtime";
var SLIDER_EMIT_THROTTLE_MS = 1e3;
var searchFieldSx = {
  marginBottom: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main"
    }
  }
};
var SI_PREFIX_MULTIPLIERS = {
  p: 1e-12,
  n: 1e-9,
  u: 1e-6,
  \u00B5: 1e-6,
  m: 1e-3,
  k: 1e3,
  K: 1e3,
  M: 1e6,
  G: 1e9
};
var parseTypedNumber = (rawText) => {
  const normalized = rawText.trim().replace(",", ".");
  const match = normalized.match(
    /^([+-]?\d*\.?\d+(?:[eE][+-]?\d+)?)\s*([a-zA-Zµ]*)$/
  );
  if (!match) return parseFloat(normalized);
  const [, numberPart, suffix] = match;
  const numericValue = parseFloat(numberPart);
  if (!suffix) return numericValue;
  const prefixMultiplier = SI_PREFIX_MULTIPLIERS[suffix[0]];
  return prefixMultiplier !== void 0 ? numericValue * prefixMultiplier : numericValue;
};
var findClosestIndex = (options, target, multipler) => {
  if (options.length === 0) return -1;
  if (target === void 0 || target === null || target === "") return -1;
  const numericTarget = typeof target === "number" ? target : parseTypedNumber(String(target));
  if (isNaN(numericTarget)) return -1;
  const rawTarget = numericTarget * (multipler ?? 1);
  let closestIndex = 0;
  let closestDiff = Infinity;
  options.forEach((option, index) => {
    const diff = Math.abs(option.value - rawTarget);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
  });
  return closestIndex;
};
var FilterField = ({
  name,
  label,
  type,
  unit,
  onChange,
  options = [],
  value,
  multipler,
  hideLabel = false
}) => {
  const isCheckboxList = type === "checkboxlist";
  const isRange = type === "minmax";
  const isSingleSelect = !isCheckboxList && !isRange;
  const stringOptions = options;
  const numericOptions = options;
  const [selectedOptions, setSelectedOptions] = useState(
    Array.isArray(value) ? value : []
  );
  const [selectedValue, setSelectedValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sliderIndices, setSliderIndices] = useState([
    0,
    Math.max(options.length - 1, 0)
  ]);
  const [minText, setMinText] = useState("");
  const [maxText, setMaxText] = useState("");
  const throttleTimeoutRef = useRef(null);
  const lastEmitAtRef = useRef(0);
  const latestIndicesRef = useRef(sliderIndices);
  const isDraggingRef = useRef(false);
  useEffect(() => {
    if (isCheckboxList) {
      setSelectedOptions(Array.isArray(value) ? value : []);
    } else if (isRange) {
      if (isDraggingRef.current) return;
      const rangeValue = value;
      const lastIndex = Math.max(numericOptions.length - 1, 0);
      const minIndex = findClosestIndex(
        numericOptions,
        rangeValue?.min,
        multipler
      );
      const maxIndex = findClosestIndex(
        numericOptions,
        rangeValue?.max,
        multipler
      );
      const nextIndices = [
        minIndex === -1 ? 0 : minIndex,
        maxIndex === -1 ? lastIndex : maxIndex
      ];
      setSliderIndices(nextIndices);
      latestIndicesRef.current = nextIndices;
    } else {
      const matchIndex = findClosestIndex(
        numericOptions,
        value,
        multipler
      );
      setSelectedValue(
        matchIndex === -1 ? "" : numericOptions[matchIndex].value
      );
    }
  }, [type, value, options, multipler]);
  useEffect(() => {
    if (!isRange) return;
    setMinText(numericOptions[sliderIndices[0]]?.label ?? "");
    setMaxText(numericOptions[sliderIndices[1]]?.label ?? "");
  }, [isRange, sliderIndices, options]);
  useEffect(() => {
    setSearchTerm("");
  }, [options]);
  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);
  const handleListSelectionChange = (option) => {
    const currentIndex = selectedOptions.indexOf(option);
    const newSelectedOptions = [...selectedOptions];
    if (currentIndex === -1) {
      newSelectedOptions.push(option);
    } else {
      newSelectedOptions.splice(currentIndex, 1);
    }
    setSelectedOptions(newSelectedOptions);
    onChange(name, newSelectedOptions);
  };
  const handleSingleSelectChange = (optionRawValue) => {
    const isDeselecting = selectedValue === optionRawValue;
    setSelectedValue(isDeselecting ? "" : optionRawValue);
    onChange(name, isDeselecting ? "" : optionRawValue / (multipler ?? 1));
  };
  const indicesToValue = ([minIndex, maxIndex]) => {
    const lastIndex = numericOptions.length - 1;
    const m = multipler ?? 1;
    return {
      min: minIndex > 0 ? numericOptions[minIndex].value / m : "",
      max: maxIndex < lastIndex ? numericOptions[maxIndex].value / m : ""
    };
  };
  const emitSliderValue = (indices) => {
    onChange(name, indicesToValue(indices));
    lastEmitAtRef.current = Date.now();
  };
  const handleSliderChange = (event, newIndices) => {
    const indices = newIndices;
    isDraggingRef.current = true;
    setSliderIndices(indices);
    latestIndicesRef.current = indices;
    const elapsed = Date.now() - lastEmitAtRef.current;
    if (elapsed >= SLIDER_EMIT_THROTTLE_MS) {
      emitSliderValue(indices);
    } else if (!throttleTimeoutRef.current) {
      throttleTimeoutRef.current = setTimeout(() => {
        throttleTimeoutRef.current = null;
        emitSliderValue(latestIndicesRef.current);
      }, SLIDER_EMIT_THROTTLE_MS - elapsed);
    }
  };
  const handleSliderChangeCommitted = (event, newIndices) => {
    const indices = newIndices;
    isDraggingRef.current = false;
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
    setSliderIndices(indices);
    latestIndicesRef.current = indices;
    emitSliderValue(indices);
  };
  const commitRangeText = (which, rawText) => {
    const resolvedIndex = findClosestIndex(numericOptions, rawText, multipler);
    if (resolvedIndex === -1) {
      setMinText(numericOptions[sliderIndices[0]]?.label ?? "");
      setMaxText(numericOptions[sliderIndices[1]]?.label ?? "");
      return;
    }
    const [currentMin, currentMax] = sliderIndices;
    const newIndices = which === "min" ? [Math.min(resolvedIndex, currentMax), currentMax] : [currentMin, Math.max(resolvedIndex, currentMin)];
    isDraggingRef.current = false;
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
    setSliderIndices(newIndices);
    latestIndicesRef.current = newIndices;
    emitSliderValue(newIndices);
  };
  const handleRangeTextKeyDown = (event) => {
    if (event.key === "Enter") {
      event.target.blur();
    }
  };
  const renderFilterName = (name2) => {
    return name2.split(/(<sub>.*?<\/sub>)/g).map((part, index) => {
      if (part.startsWith("<sub>") && part.endsWith("</sub>")) {
        return /* @__PURE__ */ jsx("sub", { children: part.replace(/<\/?sub>/g, "") }, index);
      }
      return part;
    });
  };
  const getLabelWithUnit = (label2) => {
    return renderFilterName(unit ? `${label2} (${unit})` : label2);
  };
  let checkboxListOptions = [...stringOptions];
  if (isCheckboxList) {
    checkboxListOptions = [
      ...selectedOptions,
      ...stringOptions.filter((option) => !selectedOptions.includes(option))
    ];
  }
  const filteredCheckboxOptions = checkboxListOptions.filter(
    (option) => String(option).toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredSingleSelectOptions = isSingleSelect ? [...numericOptions].sort(
    (a, b) => a.value === selectedValue ? -1 : b.value === selectedValue ? 1 : 0
  ).filter(
    (option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];
  return /* @__PURE__ */ jsxs(Box, { children: [
    isRange && numericOptions.length >= 2 && /* @__PURE__ */ jsxs(Box, { sx: { marginBottom: 2, paddingX: 1 }, children: [
      !hideLabel && /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { marginBottom: 1 }, children: getLabelWithUnit(label ?? name) }),
      /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", gap: 1, marginBottom: 1.5 }, children: [
        /* @__PURE__ */ jsx(
          TextField,
          {
            size: "small",
            label: "Min",
            placeholder: unit ? `e.g. 220m${unit}` : void 0,
            value: minText,
            onChange: (e) => setMinText(e.target.value),
            onBlur: () => commitRangeText("min", minText),
            onKeyDown: handleRangeTextKeyDown
          }
        ),
        /* @__PURE__ */ jsx(
          TextField,
          {
            size: "small",
            label: "Max",
            placeholder: unit ? `e.g. 220m${unit}` : void 0,
            value: maxText,
            onChange: (e) => setMaxText(e.target.value),
            onBlur: () => commitRangeText("max", maxText),
            onKeyDown: handleRangeTextKeyDown
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        Slider,
        {
          size: "small",
          value: sliderIndices,
          onChange: handleSliderChange,
          onChangeCommitted: handleSliderChangeCommitted,
          min: 0,
          max: numericOptions.length - 1,
          step: 1,
          marks: true,
          disableSwap: true,
          valueLabelDisplay: "auto",
          valueLabelFormat: (index) => numericOptions[index]?.label ?? ""
        }
      ),
      /* @__PURE__ */ jsxs(Box, { sx: { display: "flex", justifyContent: "space-between" }, children: [
        /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", children: numericOptions[0].label }),
        /* @__PURE__ */ jsx(Typography, { variant: "caption", color: "text.secondary", children: numericOptions[numericOptions.length - 1].label })
      ] })
    ] }),
    isCheckboxList && /* @__PURE__ */ jsxs(Box, { children: [
      !hideLabel && /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { marginBottom: 1 }, children: getLabelWithUnit(label ?? name) }),
      /* @__PURE__ */ jsx(
        TextField,
        {
          fullWidth: true,
          size: "small",
          placeholder: "Search options...",
          variant: "outlined",
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          sx: searchFieldSx,
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx(
              SearchIcon,
              {
                fontSize: "small",
                sx: { color: "text.secondary" }
              }
            ) })
          }
        }
      ),
      /* @__PURE__ */ jsx(Paper, { elevation: 0, children: /* @__PURE__ */ jsxs(List, { dense: true, sx: { paddingY: 0 }, children: [
        filteredCheckboxOptions.map((option) => /* @__PURE__ */ jsxs(
          ListItem,
          {
            button: true,
            onClick: () => handleListSelectionChange(option),
            sx: { paddingY: 0.25 },
            children: [
              /* @__PURE__ */ jsx(ListItemIcon, { children: /* @__PURE__ */ jsx(
                Checkbox,
                {
                  edge: "start",
                  checked: selectedOptions.indexOf(option) !== -1,
                  tabIndex: -1,
                  disableRipple: true
                }
              ) }),
              /* @__PURE__ */ jsx(ListItemText, { primary: option })
            ]
          },
          option
        )),
        filteredCheckboxOptions.length === 0 && /* @__PURE__ */ jsx(ListItem, { children: /* @__PURE__ */ jsx(
          ListItemText,
          {
            primary: "No matching options",
            sx: { color: "text.secondary" }
          }
        ) })
      ] }) })
    ] }),
    isSingleSelect && numericOptions.length >= 1 && /* @__PURE__ */ jsxs(Box, { children: [
      !hideLabel && /* @__PURE__ */ jsx(Typography, { variant: "body1", sx: { marginBottom: 1 }, children: getLabelWithUnit(label ?? name) }),
      /* @__PURE__ */ jsx(
        TextField,
        {
          fullWidth: true,
          size: "small",
          placeholder: "Search options...",
          variant: "outlined",
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          sx: searchFieldSx,
          InputProps: {
            startAdornment: /* @__PURE__ */ jsx(InputAdornment, { position: "start", children: /* @__PURE__ */ jsx(
              SearchIcon,
              {
                fontSize: "small",
                sx: { color: "text.secondary" }
              }
            ) })
          }
        }
      ),
      /* @__PURE__ */ jsx(Paper, { elevation: 0, children: /* @__PURE__ */ jsxs(List, { dense: true, sx: { paddingY: 0 }, children: [
        filteredSingleSelectOptions.map((option) => /* @__PURE__ */ jsxs(
          ListItem,
          {
            button: true,
            onClick: () => handleSingleSelectChange(option.value),
            sx: { paddingY: 0.25 },
            children: [
              /* @__PURE__ */ jsx(ListItemIcon, { children: /* @__PURE__ */ jsx(
                Checkbox,
                {
                  edge: "start",
                  checked: selectedValue === option.value,
                  tabIndex: -1,
                  disableRipple: true
                }
              ) }),
              /* @__PURE__ */ jsx(ListItemText, { primary: option.label })
            ]
          },
          option.value
        )),
        filteredSingleSelectOptions.length === 0 && /* @__PURE__ */ jsx(ListItem, { children: /* @__PURE__ */ jsx(
          ListItemText,
          {
            primary: "No matching options",
            sx: { color: "text.secondary" }
          }
        ) })
      ] }) })
    ] })
  ] });
};
var FilterField_default = FilterField;

// src/UberFilter/filterOptions.ts
var getUniqueOptions = (data, columnName) => {
  const isCertificates = columnName === "label";
  if (isCertificates) {
    columnName = "Certificates";
  }
  const rawByDisplay = /* @__PURE__ */ new Map();
  const isValidOption = (v) => v !== null && v !== void 0 && v !== "";
  data.forEach((item) => {
    const params = getItemParams(item);
    if (isCertificates) {
      if (params && Array.isArray(params.Certificates)) {
        params.Certificates.forEach((cert) => {
          if (cert && isValidOption(cert.label) && !rawByDisplay.has(cert.label)) {
            rawByDisplay.set(cert.label, void 0);
          }
        });
      }
      return;
    }
    const rawValue = params.Parameters?.[columnName];
    const uiValue = params.ParametersUI?.[columnName];
    if (params && uiValue && Array.isArray(uiValue)) {
      uiValue.forEach((display) => {
        if (isValidOption(display) && !rawByDisplay.has(String(display))) {
          rawByDisplay.set(String(display), rawValue);
        }
      });
    } else if (params && uiValue) {
      const display = uiValue;
      if (isValidOption(display) && !rawByDisplay.has(String(display))) {
        rawByDisplay.set(String(display), rawValue);
      }
    } else if (isValidOption(rawValue)) {
      if (!rawByDisplay.has(String(rawValue))) {
        rawByDisplay.set(String(rawValue), rawValue);
      }
    }
  });
  const uniqueArray = Array.from(rawByDisplay.keys());
  uniqueArray.sort((a, b) => {
    const rawA = parseFloat(String(rawByDisplay.get(a)));
    const rawB = parseFloat(String(rawByDisplay.get(b)));
    if (!isNaN(rawA) && !isNaN(rawB)) {
      return rawA - rawB;
    }
    return String(a).localeCompare(String(b), void 0, { numeric: true });
  });
  return uniqueArray;
};
var getUniqueNumericOptions = (data, columnName) => {
  const isValidNumber = (v) => typeof v === "number" && !isNaN(v);
  const byValue = /* @__PURE__ */ new Map();
  data.forEach((item) => {
    const params = getItemParams(item);
    const rawValue = params.Parameters?.[columnName];
    if (!isValidNumber(rawValue)) return;
    const uiValue = params.ParametersUI?.[columnName];
    const uiDisplay = Array.isArray(uiValue) ? uiValue[0] : uiValue;
    const label = uiDisplay !== null && uiDisplay !== void 0 && uiDisplay !== "" ? String(uiDisplay) : String(rawValue);
    byValue.set(rawValue, { label });
  });
  return Array.from(byValue.entries()).map(([value, { label }]) => ({ value, label })).sort((a, b) => a.value - b.value);
};
var getEffectiveFilterType = (data, control) => {
  if (control.filterType === "checkboxlist" || control.filterType === "minmax") {
    return control.filterType;
  }
  return getUniqueNumericOptions(data, control.column).length > 0 ? "eq" : "checkboxlist";
};

// src/UberFilter/FilterPopover.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var FilterPopover = ({
  open,
  anchorEl,
  handleClose,
  filterGroup,
  onFilterChange,
  optionsData,
  filterValues
}) => {
  const theme = useTheme();
  const renderFilterName = (name) => {
    return name.split(/(<sub>.*?<\/sub>)/g).map((part, index) => {
      if (part.startsWith("<sub>") && part.endsWith("</sub>")) {
        return /* @__PURE__ */ jsx2("sub", { children: part.replace(/<\/?sub>/g, "") }, index);
      }
      return part;
    });
  };
  return /* @__PURE__ */ jsxs2(
    Popover,
    {
      elevation: 0,
      open,
      anchorEl,
      onClose: handleClose,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      PaperProps: {
        sx: {
          marginTop: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          width: 360,
          maxWidth: "90vw",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column"
        }
      },
      children: [
        /* @__PURE__ */ jsxs2(
          Box2,
          {
            padding: 2,
            sx: {
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0,
              ...scrollbarSx_default
            },
            children: [
              /* @__PURE__ */ jsxs2(Typography2, { variant: "h6", gutterBottom: true, children: [
                "Enter ",
                renderFilterName(filterGroup.name)
              ] }),
              filterGroup.controls.map((control) => {
                const effectiveType = getEffectiveFilterType(optionsData, control);
                const options = effectiveType === "checkboxlist" ? getUniqueOptions(optionsData, control.column) : getUniqueNumericOptions(optionsData, control.column);
                const minRequiredOptions = effectiveType === "minmax" ? 2 : 1;
                if (options.length < minRequiredOptions) {
                  return null;
                }
                return /* @__PURE__ */ jsx2(
                  FilterField_default,
                  {
                    name: control.column,
                    label: control.column === "label" ? "Certificates" : void 0,
                    type: effectiveType,
                    unit: control.basicUnit,
                    options,
                    value: filterValues[control.column],
                    onChange: onFilterChange,
                    multipler: control.multipler,
                    hideLabel: filterGroup.controls.length === 1
                  },
                  control.column
                );
              })
            ]
          }
        ),
        /* @__PURE__ */ jsx2(
          Stack,
          {
            direction: "row",
            spacing: 2,
            padding: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            children: /* @__PURE__ */ jsx2(
              Button,
              {
                fullWidth: true,
                color: "primary",
                variant: "contained",
                sx: { fontWeight: 700 },
                onClick: handleClose,
                children: "Close"
              }
            )
          }
        )
      ]
    }
  );
};
var FilterPopover_default = FilterPopover;

// src/UberFilter/UberFilterGroup.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var isEmptyValue = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === "" || value === null || value === void 0;
};
var getControlKeys = (control, effectiveType) => {
  if (effectiveType === "checkboxlist") {
    const isCertificates = control.column === "label";
    return [
      {
        columnName: isCertificates ? "Certificates" : `ParametersUI.${control.column}`,
        operator: isCertificates ? "contains" : "in"
      }
    ];
  }
  if (effectiveType === "minmax") {
    const columnName = `Parameters.${control.column}`;
    return [
      { columnName, operator: "gte" },
      { columnName, operator: "lte" }
    ];
  }
  return [{ columnName: `Parameters.${control.column}`, operator: "eq" }];
};
var UberFilterGroup = ({
  optionsData,
  filterGroup,
  selectedFilters,
  callback
}) => {
  const theme = useTheme2();
  const [open, setOpen] = useState2(false);
  const [anchorEl, setAnchorEl] = useState2(null);
  const [filterValues, setFilterValues] = useState2({});
  const renderFilterName = (name) => {
    return name.split(/(<sub>.*?<\/sub>)/g).map((part, index) => {
      if (part.startsWith("<sub>") && part.endsWith("</sub>")) {
        return /* @__PURE__ */ jsx3("sub", { children: part.replace(/<\/?sub>/g, "") }, index);
      }
      return part;
    });
  };
  useEffect2(() => {
    const existingFilterValues = {};
    selectedFilters.forEach((filter) => {
      const control = filterGroup.controls.find(
        (control2) => getControlKeys(
          control2,
          getEffectiveFilterType(optionsData, control2)
        ).some(
          (key) => key.columnName === filter.columnName && key.operator === filter.operator
        )
      );
      if (control) {
        const effectiveType = getEffectiveFilterType(optionsData, control);
        const multipler = control.multipler ?? 1;
        const filterValue = filter.value;
        const unmultipliedValue = filterValue / multipler;
        if (effectiveType === "minmax") {
          const existing = existingFilterValues[control.column] ?? {};
          if (filter.operator === "gte") {
            existing.min = unmultipliedValue;
          } else if (filter.operator === "lte") {
            existing.max = unmultipliedValue;
          }
          existingFilterValues[control.column] = existing;
        } else if (effectiveType === "checkboxlist") {
          existingFilterValues[control.column] = filter.value;
        } else {
          existingFilterValues[control.column] = Array.isArray(filterValue) ? filterValue.map((v) => v / multipler) : unmultipliedValue;
        }
      }
    });
    setFilterValues(existingFilterValues);
  }, [selectedFilters, filterGroup, optionsData]);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const hasVisibleControls = filterGroup.controls.some((control) => {
    const effectiveType = getEffectiveFilterType(optionsData, control);
    const minRequiredOptions = effectiveType === "minmax" ? 2 : 1;
    const optionsCount = effectiveType === "checkboxlist" ? getUniqueOptions(optionsData, control.column).length : getUniqueNumericOptions(optionsData, control.column).length;
    return optionsCount >= minRequiredOptions;
  });
  const handleFilterChange = (name, value) => {
    const nextValues = { ...filterValues, [name]: value };
    setFilterValues(nextValues);
    const request = buildRequest(nextValues);
    const groupFilterKeys = filterGroup.controls.flatMap(
      (control) => getControlKeys(control, getEffectiveFilterType(optionsData, control))
    );
    callback && callback(request, groupFilterKeys);
  };
  const buildRequest = (filters) => {
    const request = [];
    Object.entries(filters).forEach(([key, value]) => {
      const control = filterGroup.controls.find((c) => c.column === key);
      if (!control) return;
      const effectiveType = getEffectiveFilterType(optionsData, control);
      if (effectiveType === "checkboxlist") {
        if (isEmptyValue(value)) return;
        const [{ columnName, operator }] = getControlKeys(
          control,
          effectiveType
        );
        request.push({ columnName, value, operator });
      } else if (effectiveType === "minmax") {
        const columnName = `Parameters.${control.column}`;
        const multipler = control.multipler ?? 1;
        const { min, max } = value || {};
        if (!isEmptyValue(min)) {
          request.push({
            columnName,
            value: min * multipler,
            operator: "gte"
          });
        }
        if (!isEmptyValue(max)) {
          request.push({
            columnName,
            value: max * multipler,
            operator: "lte"
          });
        }
      } else {
        if (isEmptyValue(value)) return;
        const columnName = `Parameters.${control.column}`;
        const multipler = control.multipler ?? 1;
        const newValue = Array.isArray(value) ? value.map((v) => Number(v) * multipler) : value * multipler;
        request.push({ columnName, value: newValue, operator: "eq" });
      }
    });
    return request;
  };
  if (!hasVisibleControls) return null;
  return /* @__PURE__ */ jsxs3(Box3, { display: "flex", justifyContent: "center", children: [
    /* @__PURE__ */ jsxs3(
      Box3,
      {
        display: "flex",
        alignItems: "center",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        paddingY: 1,
        paddingX: 2,
        sx: {
          cursor: "pointer",
          backgroundColor: "white",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "rgba(25, 118, 210, 0.04)",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
          }
        },
        onClick: handleClick,
        children: [
          /* @__PURE__ */ jsx3(
            Typography3,
            {
              sx: {
                color: "text.primary",
                fontWeight: 500,
                fontSize: "0.875rem"
              },
              children: renderFilterName(filterGroup.name)
            }
          ),
          /* @__PURE__ */ jsx3(
            ExpandMoreIcon,
            {
              sx: {
                marginLeft: 0.5,
                width: 16,
                height: 16,
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 0.2s ease",
                color: "text.secondary"
              }
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx3(
      FilterPopover_default,
      {
        open,
        anchorEl,
        handleClose,
        filterGroup,
        onFilterChange: handleFilterChange,
        optionsData,
        filterValues
      }
    )
  ] });
};
var UberFilterGroup_default = UberFilterGroup;

export {
  FilterField_default,
  getUniqueNumericOptions,
  getEffectiveFilterType,
  FilterPopover_default,
  UberFilterGroup_default
};
